import React from 'react';
import clsx from 'clsx';
import { PackageX, Clock, RotateCcw, XCircle, ArrowRight } from 'lucide-react';
import { STATUS_MAP } from '../../../../../utils/catalogs';
import { ATTENTION_REASON } from '../../../../../features/attention/attentionConstants';
import {
  formatNumber,
  formatRelativeTime,
} from '../../../../../features/dashboard/dashboardUtils';

const Metric = ({ icon: Icon, value, label, tone, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 text-left rounded-xl p-2 -m-2 transition-colors hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] group"
  >
    <div
      className={clsx(
        'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0',
        tone === 'danger' && 'bg-red-100 text-danger dark:bg-red-900/20',
        tone === 'warning' && 'bg-orange-100 text-warning dark:bg-orange-900/20',
        tone === 'neutral' && 'bg-ninja-gray dark:bg-[#2d2d2d] text-text-muted'
      )}
    >
      <Icon size={20} />
    </div>
    <div className="min-w-0">
      <div className="text-2xl font-extrabold text-ninja-dark dark:text-white leading-none group-hover:text-ninja-green transition-colors">
        {formatNumber(value)}
      </div>
      <div className="text-xs text-text-secondary mt-1">{label}</div>
    </div>
  </button>
);

/**
 * What the seller's shipments need, across the top of the page: the counts on
 * the left say how bad it is, the queue on the right says where to start, and
 * every one of them is a link.
 *
 * "Sin moverse" is measured from now, not from the filter.
 */
export const NeedsAttentionCard = ({
  kpis,
  items = [],
  onItemClick,
  onMetricClick,
  isLoading,
}) => {
  const totals = kpis || {};

  return (
    <div data-testid="dashboard-needs-attention" className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
      <h2 className="text-base font-bold text-ninja-dark dark:text-white mb-5">
        Necesitan tu atención
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 grid grid-cols-2 gap-x-4 gap-y-5 content-start">
          <Metric
            icon={Clock}
            value={totals.stalled}
            label="Sin moverse"
            tone="warning"
            onClick={() => onMetricClick(ATTENTION_REASON.STALLED)}
          />
          <Metric
            icon={PackageX}
            value={totals.without_tracking}
            label="Sin tracking"
            tone="neutral"
            onClick={() => onMetricClick(ATTENTION_REASON.WITHOUT_TRACKING)}
          />
          <Metric
            icon={XCircle}
            value={totals.failed}
            label="Fallidas"
            tone="danger"
            onClick={() => onMetricClick(ATTENTION_REASON.FAILED)}
          />
          <Metric
            icon={RotateCcw}
            value={totals.returning}
            label="En devolución"
            tone="warning"
            onClick={() => onMetricClick(ATTENTION_REASON.RETURNING)}
          />
        </div>

        {/* The divider only exists once the two halves sit side by side. */}
        <div className="lg:col-span-3 lg:border-l lg:border-border lg:pl-6">
          {isLoading && items.length === 0 && (
            <div className="flex flex-col gap-3">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-7 rounded-lg bg-ninja-gray dark:bg-[#2d2d2d] animate-pulse" />
              ))}
            </div>
          )}

          {!isLoading && items.length === 0 && (
            <div className="h-full flex items-center">
              <p className="text-sm text-text-muted">Nada detenido. Todo se está moviendo.</p>
            </div>
          )}

          <div className="flex flex-col gap-2.5">
            {items.map((item) => (
              <button
                key={item.delivery_id}
                data-testid="dashboard-attention-item"
                onClick={() => onItemClick(item.order_id)}
                className="flex items-baseline gap-2 text-left group"
              >
                <span className="text-sm font-medium text-ninja-dark dark:text-white group-hover:text-ninja-green transition-colors flex-shrink-0">
                  #{item.order_id}
                </span>
                <span className="text-xs text-text-secondary truncate">
                  {STATUS_MAP[item.status]?.user_name || item.status}
                  {item.commune ? ` · ${item.commune}` : ''}
                </span>
                <span className="text-xs text-text-muted ml-auto flex-shrink-0">
                  {item.last_movement ? formatRelativeTime(item.last_movement) : 'Sin historial'}
                </span>
              </button>
            ))}
          </div>

          {items.length > 0 && (
            <button
              onClick={() => onMetricClick(ATTENTION_REASON.ALL)}
              className="flex items-center gap-1.5 mt-4 text-sm font-semibold text-ninja-green hover:gap-2.5 transition-all"
            >
              Ver todos los envíos que necesitan atención
              <ArrowRight size={15} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
